/**
 * teamLiveRepository — the change signal behind the live Team & Roles surfaces.
 *
 * The ONLY Firebase importer in the teamLive trio (policy leaf → this → hook),
 * modelled on services/workspaceIndexLive.ts, including its tolerance
 * contract: any failure yields SILENCE, never a throw. If a listener cannot
 * start or dies, the surface keeps exactly today's mount-scoped behaviour.
 *
 * This is deliberately a SIGNAL, not a data source. Subscribers learn that
 * something changed and re-run the callable they already use — `listMembers`
 * joins live profile emails server-side and serves a reduced roster to members
 * without team.view, `listRoles` overrides the stale stored member counts, and
 * `getMemberLimitStatuses` runs the very evaluation the pre-turn gate runs. A
 * listener that rendered raw documents would quietly lose all of that and give
 * the account a second, drifting decider.
 *
 * No `registerFirestoreListener` (lib/auth/session.ts): every listener here is
 * strictly component-scoped and torn down by the hook's cleanup, and importing
 * that module would drag firebase/auth + the thread-persist + guest-chat graph
 * into a Team page hook for no benefit. subscribeWorkspaceNames — the closest
 * precedent — does not register either.
 */
import { getApp } from 'firebase/app';
import { getFirestore, collection, doc, onSnapshot } from 'firebase/firestore';
import type { TeamLiveKind } from '../services/teamLive/teamLivePolicy';

export type { TeamLiveKind };

export interface SubscribeTeamChangesOptions {
  /**
   * `accounts/{accountId}/account_usage/{usageMonthKey}` — the document the
   * 'accountUsage' kind watches. Supplied by the caller (never computed here)
   * so the hook owns the month-rollover re-key and this module stays a
   * mechanism.
   */
  usageMonthKey?: string;
}

interface RegistryEntry {
  unsub: () => void;
  subscribers: Set<(kind: TeamLiveKind) => void>;
  /**
   * Firestore delivers a snapshot immediately on subscribe. That first one
   * carries no news — the consumer's own mount-time load() is fetching the
   * same state right now — so it must never bump a revision, or every page
   * open would cost a second round of callables.
   */
  seenFirst: boolean;
  /** A terminal snapshot error. Kept so nothing retry-loops against rules. */
  dead: boolean;
}

/** Keyed `${accountId}::${kind}` (plus the month key for accountUsage). */
const registry = new Map<string, RegistryEntry>();

function registryKey(accountId: string, kind: TeamLiveKind, opts: SubscribeTeamChangesOptions): string {
  if (kind === 'accountUsage') return `${accountId}::accountUsage::${opts.usageMonthKey ?? ''}`;
  return `${accountId}::${kind}`;
}

/** Test hook: drops every shared listener between cases. */
export function __resetTeamLiveRegistryForTests(): void {
  registry.forEach((entry) => {
    try { entry.unsub(); } catch { /* ignore */ }
  });
  registry.clear();
}

function startListener(
  accountId: string,
  kind: TeamLiveKind,
  opts: SubscribeTeamChangesOptions,
  entry: RegistryEntry,
): () => void {
  const db = getFirestore(getApp());

  const onNext = () => {
    if (!entry.seenFirst) {
      entry.seenFirst = true;
      return;
    }
    entry.subscribers.forEach((fn) => {
      try { fn(kind); } catch { /* a subscriber must never break the listener */ }
    });
  };

  const onError = (error: unknown) => {
    // Terminal by design. Going quiet is the whole degrade path: the surface
    // falls back to its mount-scoped + manual-refresh behaviour.
    entry.dead = true;
    console.warn('[teamLive] snapshot error; live updates off for', kind, error);
  };

  if (kind === 'accountUsage') {
    const monthKey = opts.usageMonthKey;
    if (!monthKey) return () => undefined;
    return onSnapshot(doc(db, 'accounts', accountId, 'account_usage', monthKey), onNext, onError);
  }
  return onSnapshot(collection(db, 'accounts', accountId, kind), onNext, onError);
}

/**
 * Watch the given kinds for change. `onChange` fires with the kind that moved,
 * never on the initial snapshot. Several callers wanting the same
 * (accountId, kind) share ONE underlying listener, dropped when the last of
 * them unsubscribes. The returned function is always safe to call.
 */
export function subscribeTeamChanges(
  accountId: string,
  kinds: readonly TeamLiveKind[],
  onChange: (kind: TeamLiveKind) => void,
  opts: SubscribeTeamChangesOptions = {},
): () => void {
  if (!accountId || kinds.length === 0) return () => undefined;

  const joined: string[] = [];

  for (const kind of kinds) {
    const key = registryKey(accountId, kind, opts);
    if (joined.includes(key)) continue;

    let entry = registry.get(key);
    if (!entry) {
      entry = { unsub: () => undefined, subscribers: new Set(), seenFirst: false, dead: false };
      registry.set(key, entry);
      try {
        entry.unsub = startListener(accountId, kind, opts, entry);
      } catch (error) {
        // Setup threw (no Firebase app, blocked Listen channel). Silence.
        entry.dead = true;
        entry.unsub = () => undefined;
        console.warn('[teamLive] listener setup failed for', kind, error);
      }
    }
    entry.subscribers.add(onChange);
    joined.push(key);
  }

  return () => {
    for (const key of joined) {
      const entry = registry.get(key);
      if (!entry) continue;
      entry.subscribers.delete(onChange);
      if (entry.subscribers.size === 0) {
        registry.delete(key);
        try { entry.unsub(); } catch { /* ignore */ }
      }
    }
    joined.length = 0;
  };
}
